// Shared "Continue" flow for the format pages (Blessed, Bedtime, Alphabet). 2026-09.
//
// 1. Locks the button and shows the loader immediately (no double taps).
// 2. Sends the book to the backend with a submissionKey: the same book configuration
//    always sends the same key, and the backend returns the SAME book for it, so a
//    retry, a double tap or a lost response never creates a second book.
// 3. Adds it to the cart. A failure Shopify reports is shown and can be retried; if
//    there is no answer at all, the cart is checked before anything is added again,
//    so the book never ends up in the cart twice.
// Requires personalizationDB.js (loadProfile / saveProfile / deleteProfile).
(function () {
  let submitting = false;

  async function sha256Hex(text) {
    const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }

  function newSubmissionKey() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return Date.now().toString(36) + "-" + Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
  }

  // Same configuration -> same key; any change (name, photo, format...) -> new key.
  async function getSubmission(selectedFormat) {
    const p = await loadProfile();
    const fp = await sha256Hex(
      JSON.stringify([p?.name, p?.gender, p?.dedicationText, p?.skinTone || "", p?.imageBase64 || "", selectedFormat])
    );
    if (p?.submission?.fp === fp && p.submission.key) return p.submission;
    const submission = { fp, key: newSubmissionKey() };
    await saveProfile({ ...p, submission });
    return submission;
  }

  async function rememberCartAttempt(submission, pdfFileId) {
    const p = await loadProfile();
    await saveProfile({ ...p, submission: { ...submission, cartAttemptFor: pdfFileId } });
  }

  async function cartHasBook(pdfFileId) {
    try {
      const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
      const cart = await res.json();
      return (cart.items || []).some((it) => it.properties && it.properties["PDF File ID"] === pdfFileId);
    } catch (e) {
      return false;
    }
  }

  // opts: { continueBtn, loader, errorElement, selectedFormat, selectedVariantId,
  //         lineProperties, buildFormData }
  // Call it straight from the click handler, before any await.
  window.laylSubmitBook = async function (opts) {
    if (submitting) return;
    submitting = true;
    const { continueBtn, loader, errorElement, lineProperties } = opts;
    if (continueBtn) continueBtn.disabled = true;
    if (loader) loader.style.display = "flex";
    if (errorElement) errorElement.textContent = "";

    const showError = (message) => {
      if (errorElement) errorElement.textContent = message;
      if (loader) loader.style.display = "none";
      if (continueBtn) continueBtn.disabled = false;
      submitting = false;
    };

    let submission;
    let pdfFileId;
    try {
      submission = await getSubmission(opts.selectedFormat);
      const formData = await opts.buildFormData();
      formData.append("submissionKey", submission.key);
      const res = await fetch("https://api.laylbooks.com/books", { method: "POST", body: formData });
      const data = await res.json();
      console.log("Backend response:", data);
      if (!data.success || !data.data?.pdfFileId) {
        return showError("Failed to generate the PDF. Please try again.");
      }
      pdfFileId = data.data.pdfFileId;
    } catch (err) {
      console.error("Backend upload error", err);
      return showError("There was an error creating your book. Please try again.");
    }

    localStorage.setItem("backendPdfFileId", pdfFileId);
    lineProperties["PDF File ID"] = pdfFileId;

    const goToCart = async () => {
      await deleteProfile();
      window.location.href = "/cart"; // loader stays up while the cart page loads
    };

    // An earlier attempt may have added this book without us seeing the answer.
    if (submission.cartAttemptFor === pdfFileId && (await cartHasBook(pdfFileId))) {
      return goToCart();
    }
    await rememberCartAttempt(submission, pdfFileId);

    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [{ id: opts.selectedVariantId, quantity: 1, properties: lineProperties }] }),
      });
      if (res.ok) return goToCart();
      // Shopify answered and did not add it (e.g. out of stock): safe to retry.
      const body = await res.json().catch(() => ({}));
      console.error("Error adding to cart", body);
      return showError(body.description || "We couldn't add your book to the cart. Please try again.");
    } catch (error) {
      // No answer: it may or may not have been added.
      console.error("Error adding to cart", error);
      if (await cartHasBook(pdfFileId)) return goToCart();
      return showError("We couldn't add your book to the cart. Please try again.");
    }
  };
})();
